import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImagePlus, X } from 'lucide-react';

import { greyP4, greyP5 } from '../../theme';

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (name: string, description?: string, imageBase64?: string) => void;
  seriesName?: string;
  editData?: { name: string; description?: string; imageUrl?: string } | null;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onAddProduct,
  seriesName: _seriesName,
  editData,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageBase64, setImageBase64] = useState<string | undefined>(undefined);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setImageBase64(undefined);
      setImagePreview(null);
    } else if (editData) {
      setName(editData.name);
      setDescription(editData.description || '');
      if (editData.imageUrl) {
        setImagePreview(editData.imageUrl);
      }
    }
  }, [isOpen, editData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onAddProduct(name.trim(), description.trim() || undefined, imageBase64);
      setName('');
      setDescription('');
      setImageBase64(undefined);
      setImagePreview(null);
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Extract raw Base64 (without the data:image/...;base64, prefix) for the API
        const base64 = result.split(',')[1];
        setImageBase64(base64);
        setImagePreview(result); // Keep full data URI for preview
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageBase64(undefined);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        setImageBase64(base64);
        setImagePreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const isEditMode = !!editData;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-product-modal-title"
        className="w-full max-w-md rounded-lg p-6"
        style={{ backgroundColor: greyP5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 id="add-product-modal-title" className="text-lg font-semibold text-white">
            {isEditMode ? t('catalogue.editProductTitle') : t('catalogue.addProductTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-greyP3 hover:text-white transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-greyP3 mb-2">{t('catalogue.productName')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('catalogue.productNamePlaceholder')}
              className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                border focus:border-green01 focus:outline-none transition-colors"
              style={{ borderColor: greyP4 }}
              autoFocus
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-greyP3 mb-2">
              {t('catalogue.description')} ({t('common.optional')})
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('catalogue.descriptionPlaceholder')}
              rows={3}
              className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                border focus:border-green01 focus:outline-none transition-colors resize-none"
              style={{ borderColor: greyP4 }}
            />
          </div>

          {/* Image Upload */}
          <div className="mb-6">
            <label className="block text-sm text-greyP3 mb-2">
              {t('catalogue.productImage')} ({t('common.optional')})
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleImageChange}
              className="hidden"
            />
            {imagePreview ? (
              <div className="relative rounded-lg bg-black border overflow-hidden" style={{ borderColor: greyP4 }}>
                <div className="flex items-center justify-center p-4" style={{ maxHeight: '200px' }}>
                  <img
                    src={imagePreview}
                    alt="Product preview"
                    className="max-w-full max-h-[160px] object-contain rounded"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 rounded-full text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                  hover:border-green01 transition-colors"
                style={{ borderColor: greyP4 }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleImageDrop}
              >
                <ImagePlus className="w-8 h-8 mx-auto mb-2 text-greyP3" />
                <p className="text-sm text-greyP3">{t('catalogue.dragDropOrClickImage')}</p>
                <p className="text-xs text-greyP3 mt-1">{t('catalogue.supportedImageFormats')}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-full text-white text-sm hover:bg-greyP4 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-6 py-2 rounded-full bg-green01 text-black text-sm font-medium
                hover:bg-green03 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEditMode ? t('common.save') : t('catalogue.addProduct')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;
